import { useState } from 'react'
import { Loader2, Upload, X, Plus, Trash2 } from 'lucide-react'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '../ui/dialog'
import { Button } from '../ui/button'
import { Input } from '../ui/input'
import { Label } from '../ui/label'
import { Textarea } from '../ui/textarea'
import { Switch } from '../ui/switch'
import { Checkbox } from '../ui/checkbox'
import { useOrganizationsForSelect } from '../../hooks/organizations/useOrganizations'
import {
  useCreateNews,
  useUpdateNews,
  useUploadNewsImage,
  type NewsItem,
  type NewsImage,
  type NewsLink,
} from '../../hooks/news/useNews'

interface NewsFormProps {
  open: boolean
  editing: NewsItem | null
  onClose: () => void
}

export function NewsForm({ open, editing, onClose }: NewsFormProps) {
  const [title, setTitle] = useState(editing?.title ?? '')
  const [content, setContent] = useState(editing?.content ?? '')
  const [images, setImages] = useState<NewsImage[]>(editing?.images ?? [])
  const [links, setLinks] = useState<NewsLink[]>(editing?.links ?? [])
  const [isAllOrgs, setIsAllOrgs] = useState(
    editing?.is_all_organizations ?? true,
  )
  const [orgIds, setOrgIds] = useState<string[]>(editing?.organization_ids ?? [])
  const [isPublished, setIsPublished] = useState(editing?.is_published ?? false)
  const [error, setError] = useState<string | null>(null)

  const { data: organizations } = useOrganizationsForSelect(open)
  const createNews = useCreateNews()
  const updateNews = useUpdateNews()
  const uploadImage = useUploadNewsImage()

  const isSaving = createNews.isPending || updateNews.isPending

  const handleImageUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files ?? [])
    e.target.value = ''
    setError(null)
    try {
      for (const file of files) {
        const result = await uploadImage.mutateAsync(file)
        setImages((prev) => [...prev, result])
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Не удалось загрузить изображение')
    }
  }

  const removeImage = (key: string) => {
    setImages((prev) => prev.filter((img) => img.key !== key))
  }

  const addLink = () => setLinks((prev) => [...prev, { label: '', url: '' }])
  const updateLink = (index: number, field: keyof NewsLink, value: string) => {
    setLinks((prev) =>
      prev.map((link, i) => (i === index ? { ...link, [field]: value } : link)),
    )
  }
  const removeLink = (index: number) =>
    setLinks((prev) => prev.filter((_, i) => i !== index))

  const toggleOrg = (id: string) => {
    setOrgIds((prev) =>
      prev.includes(id) ? prev.filter((o) => o !== id) : [...prev, id],
    )
  }

  const handleSubmit = async () => {
    setError(null)

    if (!title.trim()) {
      setError('Укажите заголовок новости')
      return
    }
    if (!content.trim()) {
      setError('Укажите текст новости')
      return
    }
    if (!isAllOrgs && orgIds.length === 0) {
      setError('Выберите хотя бы одну организацию или включите «Для всех»')
      return
    }

    const payload = {
      title: title.trim(),
      content,
      images,
      links: links.filter((l) => l.url.trim()),
      is_all_organizations: isAllOrgs,
      organization_ids: isAllOrgs ? [] : orgIds,
      is_published: isPublished,
    }

    try {
      if (editing) {
        await updateNews.mutateAsync({ id: editing.id, data: payload })
      } else {
        await createNews.mutateAsync(payload)
      }
      onClose()
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Не удалось сохранить новость')
    }
  }

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <div>
            <DialogTitle>
              {editing ? 'Редактировать новость' : 'Новая новость'}
            </DialogTitle>
            <DialogDescription>
              Текст поддерживает Markdown. Изображения и ссылки опциональны.
            </DialogDescription>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600"
          >
            <X className="h-5 w-5" />
          </button>
        </DialogHeader>

        <div className="space-y-4">
          <div>
            <Label htmlFor="news-title">Заголовок</Label>
            <Input
              id="news-title"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="Заголовок новости"
            />
          </div>

          <div>
            <Label htmlFor="news-content">Текст (Markdown)</Label>
            <Textarea
              id="news-content"
              value={content}
              onChange={(e) => setContent(e.target.value)}
              placeholder="Текст статьи..."
              rows={8}
            />
          </div>

          {/* Изображения */}
          <div>
            <Label>Изображения</Label>
            <div className="mt-1 flex flex-wrap gap-3">
              {images.map((img) => (
                <div key={img.key} className="relative h-24 w-24">
                  <img
                    src={img.url}
                    alt=""
                    className="h-24 w-24 rounded object-cover border"
                  />
                  <button
                    type="button"
                    onClick={() => removeImage(img.key)}
                    className="absolute -right-2 -top-2 rounded-full bg-red-500 p-1 text-white"
                  >
                    <X className="h-3 w-3" />
                  </button>
                </div>
              ))}
              <label className="flex h-24 w-24 cursor-pointer flex-col items-center justify-center rounded border border-dashed border-gray-300 text-gray-400 hover:border-gray-400">
                {uploadImage.isPending ? (
                  <Loader2 className="h-5 w-5 animate-spin" />
                ) : (
                  <Upload className="h-5 w-5" />
                )}
                <span className="mt-1 text-xs">Загрузить</span>
                <input
                  type="file"
                  accept="image/*"
                  multiple
                  className="hidden"
                  onChange={handleImageUpload}
                />
              </label>
            </div>
          </div>

          {/* Ссылки */}
          <div>
            <div className="flex items-center justify-between">
              <Label>Ссылки</Label>
              <Button type="button" variant="outline" size="sm" onClick={addLink}>
                <Plus className="mr-1 h-3 w-3" /> Добавить
              </Button>
            </div>
            <div className="mt-2 space-y-2">
              {links.map((link, i) => (
                <div key={i} className="flex items-center gap-2">
                  <Input
                    value={link.label ?? ''}
                    onChange={(e) => updateLink(i, 'label', e.target.value)}
                    placeholder="Название (опц.)"
                    className="w-1/3"
                  />
                  <Input
                    value={link.url}
                    onChange={(e) => updateLink(i, 'url', e.target.value)}
                    placeholder="https://..."
                    className="flex-1"
                  />
                  <button
                    type="button"
                    onClick={() => removeLink(i)}
                    className="text-gray-400 hover:text-red-500"
                  >
                    <Trash2 className="h-4 w-4" />
                  </button>
                </div>
              ))}
            </div>
          </div>

          {/* Охват организаций */}
          <div className="rounded-lg border p-3">
            <div className="flex items-center justify-between">
              <div>
                <Label>Для всех организаций</Label>
                <p className="text-xs text-gray-500">
                  Выключите, чтобы выбрать конкретные организации
                </p>
              </div>
              <Switch checked={isAllOrgs} onCheckedChange={setIsAllOrgs} />
            </div>

            {!isAllOrgs && (
              <div className="mt-3 max-h-48 space-y-2 overflow-y-auto border-t pt-3">
                {organizations?.map((org: { id: string; name: string }) => (
                  <label
                    key={org.id}
                    className="flex cursor-pointer items-center gap-2 text-sm"
                  >
                    <Checkbox
                      checked={orgIds.includes(org.id)}
                      onCheckedChange={() => toggleOrg(org.id)}
                    />
                    {org.name}
                  </label>
                ))}
                {organizations?.length === 0 && (
                  <p className="text-xs text-gray-400">Нет организаций</p>
                )}
              </div>
            )}
          </div>

          {/* Публикация */}
          <div className="flex items-center justify-between rounded-lg border p-3">
            <div>
              <Label>Опубликовать</Label>
              <p className="text-xs text-gray-500">
                Опубликованные новости видны сотрудникам
              </p>
            </div>
            <Switch checked={isPublished} onCheckedChange={setIsPublished} />
          </div>

          {error && <p className="text-sm text-red-600">{error}</p>}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={isSaving}>
            Отмена
          </Button>
          <Button onClick={handleSubmit} disabled={isSaving}>
            {isSaving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            {editing ? 'Сохранить' : 'Создать'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
